import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface PaginationProps {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({ 
  currentPage, 
  totalItems, 
  itemsPerPage, 
  onPageChange 
}) => {
  const [inputPage, setInputPage] = useState('');
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const location = useLocation();

  const handleGoToPage = () => {
    const pageNumber = parseInt(inputPage, 10);
    if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= totalPages) {
      onPageChange(pageNumber);
      setInputPage('');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setInputPage(value);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleGoToPage();
    }
  };

  const getPageUrl = (page: number) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', page.toString());
    return `${location.pathname}?${searchParams.toString()}`;
  };

  const renderPageNumbers = () => {
    const pages = [];
    // Уменьшаем количество видимых страниц на мобильных
    const maxVisiblePages = window.innerWidth < 640 ? 2 : 4;

    // Добавляем кнопку "Prev"
    pages.push(
      <Link
        key="prev"
        to={getPageUrl(currentPage - 1)}
        onClick={(e) => {
          if (currentPage === 1) {
            e.preventDefault();
            return;
          }
          onPageChange(currentPage - 1);
        }}
        className={`border border-yellow-400 px-2 py-1 sm:px-3 rounded-md text-yellow-400 font-bold text-[16px] sm:text-[20px] cursor-pointer ${
          currentPage === 1 ? 'opacity-50 pointer-events-none' : ''
        }`}
      >
        ←
      </Link>
    );

    // Всегда показываем первую страницу
    pages.push(
      <Link
        key={1}
        to={getPageUrl(1)}
        onClick={() => onPageChange(1)}
        className={`border border-yellow-400 px-2 py-1 sm:px-3 rounded-md ${
          currentPage === 1 ? "bg-yellow-400 text-white" : "text-yellow-400"
        } font-bold text-[16px] sm:text-[20px]`}
      >
        1
      </Link>
    );

    let start = Math.max(2, currentPage - Math.floor(maxVisiblePages / 2));
    let end = Math.min(start + maxVisiblePages - 1, totalPages);

    if (currentPage > 2) {
      pages.push(
        <span key="dots-1" className="px-1 sm:px-2 text-yellow-400 font-bold text-[16px] sm:text-[20px]">
          ...
        </span>
      );
    }

    // Добавляем страницы между началом и концом
    for (let i = start; i <= end; i++) {
      if (i === 1 || i === totalPages) continue;
      pages.push(
        <Link
          key={i}
          to={getPageUrl(i)}
          onClick={() => onPageChange(i)}
          className={`border border-yellow-400 px-2 py-1 sm:px-3 rounded-md ${
            currentPage === i ? "bg-yellow-400 text-white" : "text-yellow-400"
          } font-bold text-[16px] sm:text-[20px]`}
        >
          {i}
        </Link>
      );
    }

    // Добавляем многоточие перед последней страницей, если нужно
    if (end < totalPages - 1) {
      pages.push(
        <span key="dots-2" className="px-1 sm:px-2 text-yellow-400 font-bold text-[16px] sm:text-[20px]">
          ...
        </span>
      );
    }

    // Всегда показываем последнюю страницу, если она не равна первой
    if (totalPages > 1) {
      pages.push(
        <Link
          key={totalPages}
          to={getPageUrl(totalPages)}
          onClick={() => onPageChange(totalPages)}
          className={`border border-yellow-400 px-2 py-1 sm:px-3 rounded-md ${
            currentPage === totalPages ? "bg-yellow-400 text-white" : "text-yellow-400"
          } font-bold text-[16px] sm:text-[20px]`}
        >
          {totalPages}
        </Link>
      );
    }

    // Добавляем кнопку "Next"
    pages.push(
      <Link
        key="next"
        to={getPageUrl(currentPage + 1)}
        onClick={(e) => {
          if (currentPage === totalPages) {
            e.preventDefault();
            return;
          }
          onPageChange(currentPage + 1);
        }}
        className={`border border-yellow-400 px-2 py-1 sm:px-3 rounded-md text-yellow-400 font-bold text-[16px] sm:text-[20px] cursor-pointer ${
          currentPage === totalPages ? 'opacity-50 pointer-events-none' : ''
        }`}
      >
        →
      </Link>
    );

    return pages;
  };

  if (totalPages <= 1) return null;

  return (
    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
      <div className="flex items-center gap-1 sm:gap-2">
        {renderPageNumbers()}
      </div>
      <div className="flex items-center gap-2 mt-4 sm:mt-0">
        <input
          type="text"
          value={inputPage}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          placeholder="Page"
          className="border border-yellow-400 px-2 py-1 sm:px-3 rounded-md max-w-[80px] w-full text-center text-[16px] sm:text-[20px]"
        />
        <button
          onClick={handleGoToPage}
          className="border border-yellow-400 px-2 py-1 sm:px-3 rounded-md bg-yellow-400 text-white font-bold text-[16px] sm:text-[20px]"
        >
          Go
        </button>
      </div>
    </div>
  );
};

export default Pagination;